import type { CursorPaginationResponse, QueryParams } from '~/services/api';
import type { AxiosPromise } from 'axios';
import type { BaseClientConfig, ExtendedAxiosRequestConfig } from '~/services/BaseClient';

export const threadsClientConfig: BaseClientConfig = {
    apiBase: window.Spark?.apiBase,
};

export interface Thread {
    team_id: number;
    contact_id: number;
    contact_name?: string;
    contact_phone?: string;
    message_count?: number;
    group_id?: string;
    status: string;
    last_message_id?: number;
    last_message_content?: string;
    last_message_direction?: string;
    last_message_at?: string;
    thread_claim_id?: number | null;
    assignee_id: number | null;
    unread: number;
    thread_category_id?: number | null;
    thread_section?: string;
    created_at?: string;
    updated_at?: string;
}

export interface ThreadsSummary {
    me?: number;
    unassigned?: number;
    open?: number;
    closed?: number;
    all?: number;
}

export interface ThreadFilterParams extends QueryParams {
    search?: string;
    tag?: string;
    email?: string;
    phone?: string;
    lifecycle_status?: string;
    membership_status?: string;
}

export interface ThreadsGetParams extends ThreadFilterParams {
    status?: string;
    forward?: string;
    assignee?: number;
    contact_id?: number;
    thread_section?: string;
    thread_category_id?: number;
    cursor?: string;
    limit?: number;
}

export interface ThreadsPriorityParams extends QueryParams {
    start_date?: string;
    end_date?: string;
    limit?: number;
}

export interface ThreadsPriorityResponse {
    object: string;
    data: Thread[];
}

export interface AssignPayload {
    assignee_id: number;
}

export interface SnoozePayload {
    snooze_until: string | null;
}

export interface CheckpointResponse {
    message_count?: number;
}

export interface ThreadCountsParams extends ThreadFilterParams {
    assignee?: number;
}

export interface ThreadCountItem {
    thread_section: string;
    thread_category_id: number | null;
    count: number;
}

export interface ThreadCountsResponse {
    object: string;
    team_id: number;
    group_id?: string;
    data: ThreadCountItem[];
}

export interface ThreadsClientInterface {
    getThreads(
        params?: ThreadsGetParams,
        config?: ExtendedAxiosRequestConfig
    ): AxiosPromise<CursorPaginationResponse<Thread>>;

    getSummary(config?: ExtendedAxiosRequestConfig): AxiosPromise<ThreadsSummary>;

    setState(contactId: number, state: string, groupId?: string): AxiosPromise<void>;

    assignToSelf(contactId: number, groupId?: string): AxiosPromise<void>;

    assignTo(contactId: number, payload: AssignPayload, groupId?: string): AxiosPromise<void>;

    unclaim(contactId: number, groupId?: string): AxiosPromise<void>;

    checkpointSave(contactId: number, groupId?: string): AxiosPromise<CheckpointResponse>;

    checkpointDelete(contactId: number, groupId?: string): AxiosPromise<CheckpointResponse>;

    checkpointGet(contactId: number, groupId?: string): AxiosPromise<CheckpointResponse>;

    getCounts?(params?: ThreadCountsParams, config?: ExtendedAxiosRequestConfig): AxiosPromise<ThreadCountsResponse>;

    getPriority?(
        params?: ThreadsPriorityParams,
        config?: ExtendedAxiosRequestConfig
    ): AxiosPromise<ThreadsPriorityResponse>;

    snooze?(contactId: number, payload: SnoozePayload, groupId?: string): AxiosPromise<void>;

    complete?(contactId: number, groupId?: string): AxiosPromise<void>;
}
